import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalOwnerName: "Rodríguez Díaz, Catalina",
  legalTradeName: "Académie ZEDRO",
  legalFormEs: "empresa individual",
  legalFormFr: "entreprise individuelle",
  neq: "2282259599",
  jurisdictionEs: "Québec, Canadá",
  jurisdictionFr: "Québec, Canada",
  website: "zedro.academy",
  websiteUrl: "https://zedro.academy",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
  addressEs: "412-9165 rue Lennon, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr: "412-9165, rue Lennon, Brossard (Québec) J4Y 0R9, Canada",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const TERMS_COPY = {
  es: {
    title: "Términos y Condiciones de Uso, Compra y Suscripción",
    lastUpdatedLabel: "Última actualización:",
    lastUpdated: "17 de julio de 2026",
    intro: [
      <>
        Bienvenido(a) a <strong>{OPERATOR.platformName}</strong>, plataforma
        digital de formación práctica disponible en{" "}
        <a href={OPERATOR.websiteUrl}>{OPERATOR.website}</a>. La plataforma es
        operada por <strong>{OPERATOR.legalOwnerName}</strong>,{" "}
        {OPERATOR.legalFormEs} que ejerce actividades bajo el nombre comercial
        registrado <strong>{OPERATOR.legalTradeName}</strong> en{" "}
        {OPERATOR.jurisdictionEs}.
      </>,
      <>
        Al marcar la casilla de aceptación en la Store y seleccionar
        “Continuar a Stripe”, confirmas que has leído y aceptas estos Términos,
        así como las políticas legales vinculadas. Si no estás de acuerdo, no
        debes completar la compra o suscripción ni utilizar las funciones
        reservadas a miembros.
      </>,
      <>
        Estos Términos se aplican a la zona pública del sitio, la Store, el
        proceso de compra o suscripción, la cuenta de usuario y el acceso al
        ecosistema formativo de {OPERATOR.platformName}, salvo que una condición
        específica o la ley aplicable establezca otra cosa.
      </>,
    ],
    sections: [
      {
        title: "1. Información del operador",
        paragraphs: [
          <>
            Titular de la empresa individual:{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>
          </>,
          <>
            Nombre comercial registrado:{" "}
            <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Número de empresa de Québec (NEQ):{" "}
            <strong>{OPERATOR.neq}</strong>
          </>,
          <>
            Plataforma comercial: <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Dirección comercial y postal:{" "}
            <strong>{OPERATOR.addressEs}</strong>
          </>,
          <>
            Sitio web:{" "}
            <a href={OPERATOR.websiteUrl}>{OPERATOR.website}</a>
          </>,
          <>
            Correo de soporte:{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Correo administrativo/legal:{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
      {
        title: "2. Objeto de la plataforma",
        paragraphs: [
          <>
            ZEDRO Academy ofrece un ecosistema digital de formación práctica y progresiva, compuesto por 
            sistemas, módulos, recursos, ejercicios, herramientas de reflexión, registro y aplicación, cuya
            disponibilidad puede variar según el producto, la membresía, la etapa de desarrollo y la estructura
            formativa presentada en la plataforma.
          </>,
          <>
            ZEDRO Academy no se presenta como una simple biblioteca de
            contenido, una aplicación devocional ni un curso aislado. La
            membresía da acceso a una experiencia formativa digital estructurada
            y progresiva.
          </>,
          <>
            El contenido de ZEDRO Academy es formativo, educativo e
            informativo. No constituye asesoría médica, psicológica, legal,
            financiera, terapéutica, contable, pastoral personalizada ni
            sustituto de acompañamiento profesional cuando este sea necesario.
          </>,
        ],
      },
      {
        title: "3. Idioma del contenido y del contrato",
        paragraphs: [
          <>
            Las páginas públicas, la Store, estos Términos, las políticas
            legales y la información esencial relacionada con compra, pago,
            cancelación y cuenta están disponibles en francés y español.
          </>,
          <>
            El contenido formativo principal del ecosistema se ofrece
            actualmente en español. Esta condición se informa antes de iniciar
            la suscripción.
          </>,
          <>
            La versión francesa de estos Términos está disponible antes de la
            celebración del contrato. Cuando una persona selecciona español en
            el selector de idioma y confirma su aceptación antes de ser
            redirigida a Stripe, manifiesta expresamente su voluntad de celebrar
            el contrato en español después de haber tenido acceso a la versión
            francesa.
          </>,
        ],
      },
      {
        title: "4. Acceso progresivo al contenido",
        paragraphs: [
          <>
            ZEDRO Academy está diseñada como una formación guiada y progresiva.
            Por esta razón, el acceso a sistemas, días, módulos, ejercicios,
            micro-misiones, recursos o contenidos puede activarse de forma
            gradual según la estructura definida por la plataforma.
          </>,
          <>
            La adquisición de una membresía no implica acceso inmediato a todos
            los sistemas, días, recursos o contenidos futuros de ZEDRO Academy.
            El contenido puede liberarse por etapas, periodos, días de avance,
            configuración interna o disponibilidad definida por la plataforma.
          </>,
          <>
            Esta estructura busca favorecer la aplicación práctica, la
            constancia y el proceso formativo. ZEDRO Academy no está diseñada
            como una biblioteca para consumo intensivo, descarga masiva o
            finalización acelerada de todos los contenidos en un corto periodo
            de tiempo.
          </>,
          <>
            Los horarios, ritmos de activación, secuencia de contenidos y
            disponibilidad de sistemas podrán ajustarse por razones pedagógicas,
            técnicas, operativas, de seguridad o de mejora de la experiencia,
            sin que ello implique incumplimiento del servicio cuando el acceso
            se mantenga conforme a la estructura ofrecida y a la ley aplicable.
          </>,
        ],
      },
      {
        title: "5. Registro y cuenta de usuario",
        paragraphs: [
          <>
            Para acceder a ciertas funciones, contenidos o membresías, deberás
            crear una cuenta con información veraz, actualizada y completa.
            Eres responsable de mantener la confidencialidad de tus credenciales
            de acceso y de toda actividad realizada desde tu cuenta.
          </>,
          <>
            El correo electrónico ingresado durante la compra o acceso puede
            utilizarse para verificar si ya existe una cuenta, una membresía
            activa o una suscripción que deba renovarse.
          </>,
          <>
            Si sospechas acceso no autorizado, pérdida de contraseña o uso
            indebido de tu cuenta, deberás comunicarlo a ZEDRO Academy lo antes
            posible a través de los canales de contacto indicados en estos
            Términos.
          </>,
        ],
      },
      {
        title: "6. Uso permitido",
        paragraphs: [
          <>
            Al usar ZEDRO Academy, aceptas utilizar la plataforma de manera
            lícita, respetuosa y coherente con su propósito formativo.
          </>,
        ],
        list: [
          "No intentar vulnerar, copiar, dañar o interferir la plataforma.",
          "No introducir malware, automatizaciones abusivas o código malicioso.",
          "No reproducir, vender, distribuir o explotar contenidos sin autorización escrita.",
          "No compartir tu cuenta con terceros ni permitir accesos no autorizados.",
          "No usar la metodología, materiales o contenidos con fines comerciales, institucionales o de formación externa sin permiso escrito.",
          "No publicar o almacenar contenido ilegal, abusivo, discriminatorio, ofensivo, engañoso o que vulnere derechos de terceros.",
        ],
      },
      {
        title: "7. Propiedad intelectual",
        paragraphs: [
          <>
            Todos los contenidos, textos, diseños, metodologías, estructura de
            sistemas, nombres de módulos, imágenes, recursos, interfaces,
            software, bases visuales, materiales formativos y elementos de marca
            disponibles en ZEDRO Academy pertenecen a ZEDRO Academy, a
            Académie ZEDRO o a sus respectivos licenciantes.
          </>,
          <>
            La compra de una membresía o el acceso a la plataforma no transfiere
            derechos de propiedad intelectual. Se concede únicamente una
            licencia limitada, personal, revocable, no exclusiva y no
            transferible para acceder y utilizar los contenidos dentro de la
            plataforma durante el periodo de acceso correspondiente.
          </>,
        ],
      },
      {
        title: "8. Contenido creado por el usuario",
        paragraphs: [
          <>
            Algunas funciones de ZEDRO Academy permiten escribir, registrar,
            guardar o generar contenido personal, como respuestas, reflexiones,
            bitácora, evidencias, micro-misiones, peticiones, decisiones,
            registros de avance y preferencias de lectura.
          </>,
          <>
            El contenido que ingreses sigue siendo tuyo. Sin embargo, otorgas a
            ZEDRO Academy una licencia limitada para alojar, almacenar,
            procesar, mostrar y utilizar ese contenido dentro de tu cuenta con
            el fin de operar la plataforma, guardar tu progreso, personalizar tu
            experiencia, prestar soporte, mantener seguridad técnica y cumplir
            obligaciones legales.
          </>,
          <>
            Eres responsable del contenido que ingresas en la plataforma. No
            debes introducir información que vulnere derechos de terceros ni
            contenido ilegal, ofensivo, dañino o contrario al uso permitido.
          </>,
        ],
      },
      {
        title: "9. Privacidad y datos personales",
        paragraphs: [
          <>
            ZEDRO Academy puede recopilar y almacenar información necesaria para
            operar la plataforma, incluyendo información de cuenta, correo
            electrónico, estado de membresía, progreso, respuestas, registros de
            bitácora, preferencias, actividad dentro de la plataforma, datos
            técnicos y comunicaciones de soporte.
          </>,
          <>
            La información ingresada por los usuarios puede quedar almacenada en
            bases de datos y servicios tecnológicos utilizados por ZEDRO Academy
            para prestar el servicio. El tratamiento de la información personal
            se explica con mayor detalle en la Política de Privacidad.
          </>,
          <>
            ZEDRO Academy implementa medidas razonables de seguridad para
            proteger la información. Sin embargo, ningún sistema digital puede
            garantizar seguridad, disponibilidad o recuperación absoluta frente
            a fallas técnicas, errores humanos, pérdida de acceso, ataques,
            incidentes de seguridad o eventos fuera de control razonable.
          </>,
        ],
      },
      {
        title: "10. Funciones basadas en IA",
        paragraphs: [
          <>
            Algunas funciones actuales o futuras de ZEDRO Academy pueden
            utilizar herramientas de inteligencia artificial para apoyar la
            organización, reflexión, búsqueda, escritura o acompañamiento
            general del usuario.
          </>,
          <>
            Las respuestas generadas con apoyo de IA pueden contener errores,
            imprecisiones o interpretaciones incompletas. El usuario debe usar
            su criterio y no debe considerar estos resultados como asesoría
            profesional, médica, psicológica, legal, financiera o espiritual
            personalizada.
          </>,
          <>
            Recomendamos no ingresar información altamente sensible o innecesaria
            en funciones asistidas por IA.
          </>,
        ],
      },
      {
        title: "11. Productos y servicios pagos, precio, moneda e impuestos",
        paragraphs: [
          <>
            {OPERATOR.platformName} puede ofrecer membresías, productos
            digitales y otros servicios pagos. El precio, la moneda, los
            impuestos aplicables, la modalidad de pago único o recurrente, la
            periodicidad de facturación, las restricciones y las condiciones
            particulares de cada oferta se muestran en la Store y durante el
            proceso de pago antes de que el usuario confirme la transacción.
          </>,
          <>
            El precio aplicable es el que el usuario haya podido revisar y
            aceptar al completar la compra o suscripción. Un cambio posterior
            del precio público no modifica retroactivamente una transacción ya
            completada ni un periodo ya pagado.
          </>,
          <>
            Antes de completar el pago, la Store y Stripe permiten revisar la
            descripción de la oferta, el importe aplicable, la moneda, la
            modalidad de cobro y las demás condiciones esenciales de la
            transacción.
          </>,
          <>
            El banco, el emisor de la tarjeta u otros terceros pueden aplicar
            cargos externos, como conversión de moneda, que no son controlados
            por {OPERATOR.platformName}.
          </>,
          <>
            Los pagos son procesados por Stripe. {OPERATOR.platformName} no
            almacena números completos de tarjetas de crédito o débito.
          </>,
        ],
      },
      {
        title:
          "12. Prueba gratuita exclusiva de la membresía, reactivación, renovación y confirmación",
        paragraphs: [
          <>
            La prueba gratuita de 7 días corresponde exclusivamente a la
            membresía de {OPERATOR.platformName}. No se aplica a otros productos
            o servicios vendidos por separado.
          </>,
          <>
            Un usuario elegible que no haya utilizado anteriormente la prueba
            puede acceder a esos 7 días gratuitos con una tarjeta registrada.
            Durante ese periodo no se realiza el primer cobro de la membresía.
          </>,
          <>
            La prueba gratuita se concede una sola vez por usuario elegible. Si
            la persona ya utilizó la prueba o reactiva una membresía cancelada,
            la nueva suscripción se cobra directamente al precio mostrado antes
            de la confirmación.
          </>,
          <>
            Antes de confirmar la suscripción se muestran la duración de la
            prueba, el precio que se cobrará al finalizarla, la periodicidad de
            renovación y la fecha prevista del primer cobro.
          </>,
          <>
            Si el usuario cancela antes de la fecha y hora de finalización
            indicadas en la confirmación, no se realiza el primer cobro de la
            membresía.
          </>,
          <>
            Si no cancela antes de finalizar la prueba, la membresía se activa
            automáticamente al precio, periodicidad e impuestos aplicables
            mostrados y aceptados antes de confirmar la suscripción.
          </>,
          <>
            Después del primer cobro, la membresía se renueva con la periodicidad
            aceptada hasta que el usuario la cancele o hasta que el servicio
            termine conforme a estos Términos y a la ley aplicable.
          </>,
          <>
            Después de confirmar la suscripción, {OPERATOR.legalTradeName}
            transmite al correo proporcionado una confirmación que puede
            conservarse e imprimirse, con la información esencial de la
            membresía, las fechas aplicables, el precio aceptado, la renovación,
            la cancelación y el acceso a los documentos legales vigentes.
          </>,
        ],
      },
      {
        title: "13. Cancelación y reembolsos",
        paragraphs: [
          <>
            Puedes cancelar la membresía desde “Mi cuenta” o escribiendo a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>,
          <>
            La cancelación evita futuras renovaciones. Si se realiza durante la
            prueba gratuita antes de su finalización, no se efectúa el primer
            cobro.
          </>,
          <>
            Si cancelas después de un cobro mensual, conservarás el acceso hasta
            el final del periodo pagado, salvo que la ley aplicable reconozca un
            derecho distinto.
          </>,
          <>
            No se realizan reembolsos proporcionales por días no utilizados ni
            por cancelación anticipada del mes en curso, salvo cuando la ley
            aplicable lo exija.
          </>,
          <>
            Los errores de cobro, cargos duplicados, fraude comprobado o
            situaciones sujetas a un derecho legal de resolución o reembolso se
            revisarán y ajustarán cuando corresponda.
          </>,
          <>
            Si {OPERATOR.legalTradeName} termina definitivamente el servicio sin
            incumplimiento del usuario y no puede prestar una parte ya pagada,
            realizará el ajuste o reembolso exigido por la ley aplicable.
          </>,
        ],
      },
      {
        title: "14. Disputas de pago y contracargos",
        paragraphs: [
          <>
            Los pagos realizados mediante tarjeta u otros métodos procesados por
            proveedores externos pueden estar sujetos a procesos de disputa,
            contracargo o revisión por parte del emisor del método de pago, las
            redes de pago o el proveedor correspondiente.
          </>,
          <>
            Si se inicia una disputa o contracargo, ZEDRO Academy podrá
            presentar evidencia del acceso otorgado, aceptación de términos, uso
            de la plataforma, historial de facturación y comunicaciones
            relacionadas, de acuerdo con las reglas del proveedor de pago y la
            ley aplicable.
          </>,
        ],
      },
      {
        title: "15. Disponibilidad, cambios y mejoras",
        paragraphs: [
          <>
            {OPERATOR.platformName} procura mantener la plataforma disponible y
            funcional, pero no garantiza ausencia total de errores,
            compatibilidad con todos los dispositivos ni funcionamiento
            ininterrumpido.
          </>,
          <>
            Se pueden realizar mantenimientos, correcciones, mejoras de diseño,
            actualizaciones de contenido y ajustes técnicos o de seguridad que
            no reduzcan la naturaleza esencial del servicio contratado.
          </>,
          <>
            Las modificaciones del precio, de las obligaciones principales o de
            otros elementos contractuales se rigen por la sección 20 de estos
            Términos.
          </>,
        ],
      },
      {
        title: "16. Resultados personales",
        paragraphs: [
          <>
            ZEDRO Academy ofrece formación, estructura y herramientas prácticas,
            pero no garantiza resultados específicos. Los avances personales
            dependen de múltiples factores, incluyendo la constancia del
            usuario, su contexto, decisiones, aplicación práctica y
            circunstancias personales.
          </>,
          <>
            Ningún contenido debe interpretarse como promesa de transformación
            automática, resultado espiritual, emocional, económico, profesional,
            relacional, físico o personal garantizado.
          </>,
        ],
      },
      {
        title: "17. Suspensión o terminación de acceso",
        paragraphs: [
          <>
            {OPERATOR.legalTradeName} podrá suspender o limitar una cuenta por
            incumplimiento de estos Términos, falta de pago, fraude, abuso,
            riesgo de seguridad, requerimiento legal o conducta que afecte la
            plataforma o a terceros.
          </>,
          <>
            Cuando sea razonable, se notificará al usuario. En casos urgentes de
            fraude, abuso o seguridad, la medida podrá adoptarse sin aviso
            previo, en la medida permitida por la ley.
          </>,
          <>
            Cuando la suspensión o terminación sea imputable al usuario, no
            elimina las obligaciones de pago ya nacidas ni genera
            automáticamente un reembolso, sin perjuicio de los derechos
            obligatorios aplicables.
          </>,
        ],
      },
      {
        title: "18. Enlaces y servicios de terceros",
        paragraphs: [
          <>
            La plataforma puede integrar o enlazar servicios externos, como
            procesadores de pago, proveedores de correo, almacenamiento,
            analítica, alojamiento, herramientas de comunicación o recursos
            complementarios. El uso de dichos servicios puede estar sujeto a sus
            propios términos y políticas.
          </>,
          <>
            ZEDRO Academy no controla completamente las prácticas,
            disponibilidad o políticas de terceros, aunque busca trabajar con
            proveedores confiables y razonables para la operación de la
            plataforma.
          </>,
        ],
      },
      {
        title: "19. Responsabilidad",
        paragraphs: [
          <>
            En la medida permitida por la ley,{" "}
            {OPERATOR.legalTradeName} no será responsable por pérdidas indirectas
            derivadas de eventos fuera de su control razonable, del uso
            contrario a estos Términos o de decisiones tomadas exclusivamente
            por el usuario a partir de contenido general.
          </>,
          <>
            Nada en estos Términos excluye o limita la responsabilidad por falta
            intencional o falta grave, por perjuicio corporal o moral, ni
            derechos u obligaciones que no puedan excluirse o limitarse por la
            ley.
          </>,
        ],
      },
      {
        title: "20. Modificaciones de estos Términos o de la membresía",
        paragraphs: [
          <>
            En la medida permitida por la ley, podrán modificarse los siguientes
            elementos: el precio y la periodicidad futura, las funcionalidades o
            recursos incluidos, las reglas de acceso progresivo, los requisitos
            técnicos y operativos, y las disposiciones de estos Términos.
          </>,
          <>
            Cuando una modificación aumente las obligaciones del usuario o
            reduzca las obligaciones del operador, se enviará un aviso escrito,
            claro y legible, al menos 30 días antes de su entrada en vigor. El
            aviso contendrá la cláusula nueva o modificada, la versión anterior,
            la fecha de entrada en vigor y los derechos del usuario.
          </>,
          <>
            El usuario podrá rechazar la modificación y cancelar la membresía
            sin gastos, penalidad ni indemnización, mediante aviso enviado a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>{" "}
            a más tardar 30 días después de la entrada en vigor de la
            modificación, cuando esta aumente sus obligaciones o reduzca las del
            operador.
          </>,
          <>
            Ninguna modificación se aplicará retroactivamente a un periodo ya
            pagado, salvo cuando la ley lo permita o exija.
          </>,
        ],
      },
      {
        title: "21. Legislación aplicable",
        paragraphs: [
          <>
            Estos Términos se interpretan conforme a las leyes aplicables en la
            provincia de Québec y las leyes federales de Canadá que
            correspondan, sin perjuicio de los derechos obligatorios que puedan
            proteger al consumidor según su lugar de residencia.
          </>,
          <>
            Si alguna disposición de estos Términos se considera inválida o
            inaplicable, las demás disposiciones conservarán su validez en la
            máxima medida permitida por la ley.
          </>,
        ],
      },
      {
        title: "22. Contacto",
        paragraphs: [
          <>
            Para consultas sobre estos Términos, membresías, pagos,
            cancelaciones o acceso, escribe a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>,
          <>
            Para asuntos administrativos o legales, escribe a{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
            .
          </>,
        ],
      },
    ],
  },
  fr: {
    title: "Conditions d’utilisation, d’achat et d’abonnement",
    lastUpdatedLabel: "Dernière mise à jour :",
    lastUpdated: "17 juillet 2026",
    intro: [
      <>
        Bienvenue sur <strong>{OPERATOR.platformName}</strong>, une plateforme
        numérique de formation pratique accessible à l’adresse{" "}
        <a href={OPERATOR.websiteUrl}>{OPERATOR.website}</a>. La plateforme est
        exploitée par <strong>{OPERATOR.legalOwnerName}</strong>,{" "}
        {OPERATOR.legalFormFr} faisant affaire sous le nom commercial enregistré{" "}
        <strong>{OPERATOR.legalTradeName}</strong> au{" "}
        {OPERATOR.jurisdictionFr}.
      </>,
      <>
        En cochant la case d’acceptation dans la Boutique et en sélectionnant
        « Continuer vers Stripe », vous confirmez avoir lu et accepté les
        présentes Conditions ainsi que les politiques juridiques qui y sont
        liées. Si vous n’êtes pas d’accord, vous ne devez pas souscrire
        l’achat ou l’abonnement ni utiliser les fonctions réservées aux
        membres.
      </>,
      <>
        Les présentes Conditions s’appliquent à la partie publique du site, à
        la Boutique, au processus d’achat ou d’abonnement, au compte
        utilisateur et à l’accès à l’écosystème de formation de
        {OPERATOR.platformName}, sauf disposition particulière ou exigence
        contraire de la loi applicable.
      </>,
    ],
    sections: [
      {
        title: "1. Identification de l’exploitante",
        paragraphs: [
          <>
            Titulaire de l’entreprise individuelle :{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>
          </>,
          <>
            Nom commercial enregistré :{" "}
            <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Numéro d’entreprise du Québec (NEQ) :{" "}
            <strong>{OPERATOR.neq}</strong>
          </>,
          <>
            Plateforme commerciale : <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Adresse commerciale et postale :{" "}
            <strong>{OPERATOR.addressFr}</strong>
          </>,
          <>
            Site Web :{" "}
            <a href={OPERATOR.websiteUrl}>{OPERATOR.website}</a>
          </>,
          <>
            Service à la clientèle :{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Questions administratives et juridiques :{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
      {
        title: "2. Objet de la plateforme",
        paragraphs: [
          <>
            ZEDRO Academy offre un écosystème numérique de formation pratique et progressive composé de 
            systèmes, de modules, de ressources, d’exercices ainsi que d’outils de réflexion, de consignation et de 
            mise en pratique. Leur disponibilité peut varier selon le produit, l’abonnement, l’étape de développement 
            et la structure de formation présentée dans la plateforme.
          </>,
          <>
            ZEDRO Academy ne se présente pas comme une simple bibliothèque de
            contenu, une application dévotionnelle ni un cours isolé.
            L’abonnement donne accès à une expérience formative numérique
            structurée et progressive.
          </>,
          <>
            Le contenu de ZEDRO Academy est formatif, éducatif et informatif. Il
            ne constitue pas un avis médical, psychologique, juridique,
            financier, thérapeutique, comptable, pastoral personnalisé ni un
            substitut à un accompagnement professionnel lorsque celui-ci est
            nécessaire.
          </>,
        ],
      },
      {
        title: "3. Langue du contenu et du contrat",
        paragraphs: [
          <>
            Les pages publiques, la Boutique, les présentes Conditions, les
            politiques juridiques et les renseignements essentiels liés à
            l’achat, au paiement, à l’annulation et au compte sont offerts en
            français et en espagnol.
          </>,
          <>
            Le contenu principal de formation de l’écosystème est actuellement
            offert en espagnol. Cette condition est communiquée avant la
            souscription.
          </>,
          <>
            La version française des présentes Conditions est accessible avant
            la conclusion du contrat. Lorsqu’une personne choisit l’espagnol
            dans le sélecteur de langue et confirme son acceptation avant d’être
            redirigée vers Stripe, elle exprime sa volonté expresse de conclure
            le contrat en espagnol après avoir eu accès à la version française.
          </>,
        ],
      },
      {
        title: "4. Accès progressif au contenu",
        paragraphs: [
          <>
            ZEDRO Academy est conçue comme une formation guidée et progressive.
            Pour cette raison, l’accès aux systèmes, journées, modules,
            exercices, micro-missions, ressources ou contenus peut être activé
            graduellement selon la structure définie par la plateforme.
          </>,
          <>
            L’achat d’un abonnement n’implique pas un accès immédiat à tous les
            systèmes, journées, ressources ou contenus futurs de ZEDRO Academy.
            Le contenu peut être libéré par étapes, périodes, journées de
            progression, configuration interne ou disponibilité définie par la
            plateforme.
          </>,
          <>
            Cette structure vise à favoriser l’application pratique, la
            constance et le processus formatif. ZEDRO Academy n’est pas conçue
            comme une bibliothèque destinée à la consommation intensive, au
            téléchargement massif ou à l’achèvement accéléré de tous les
            contenus en peu de temps.
          </>,
          <>
            Les horaires, rythmes d’activation, séquences de contenu et
            disponibilités des systèmes peuvent être ajustés pour des raisons
            pédagogiques, techniques, opérationnelles, de sécurité ou
            d’amélioration de l’expérience, sans que cela constitue un manquement
            au service lorsque l’accès demeure conforme à la structure offerte
            et à la loi applicable.
          </>,
        ],
      },
      {
        title: "5. Inscription et compte utilisateur",
        paragraphs: [
          <>
            Pour accéder à certaines fonctions, contenus ou abonnements, vous
            devez créer un compte avec des informations exactes, à jour et
            complètes. Vous êtes responsable de la confidentialité de vos
            identifiants et de toute activité effectuée depuis votre compte.
          </>,
          <>
            L’adresse courriel saisie lors de l’achat ou de l’accès peut être
            utilisée pour vérifier s’il existe déjà un compte, un abonnement
            actif ou une souscription à renouveler.
          </>,
          <>
            Si vous soupçonnez un accès non autorisé, une perte de mot de passe
            ou une utilisation abusive de votre compte, vous devez communiquer
            avec ZEDRO Academy dès que possible au moyen des coordonnées
            indiquées dans les présentes Conditions.
          </>,
        ],
      },
      {
        title: "6. Utilisation permise",
        paragraphs: [
          <>
            En utilisant ZEDRO Academy, vous acceptez d’utiliser la plateforme
            de manière licite, respectueuse et conforme à son objectif formatif.
          </>,
        ],
        list: [
          "Ne pas tenter de compromettre, copier, endommager ou perturber la plateforme.",
          "Ne pas introduire de logiciels malveillants, d’automatisations abusives ou de code malveillant.",
          "Ne pas reproduire, vendre, distribuer ou exploiter les contenus sans autorisation écrite.",
          "Ne pas partager votre compte avec des tiers ni permettre des accès non autorisés.",
          "Ne pas utiliser la méthodologie, les documents ou les contenus à des fins commerciales, institutionnelles ou de formation externe sans autorisation écrite.",
          "Ne pas publier ni stocker de contenu illégal, abusif, discriminatoire, offensant, trompeur ou portant atteinte aux droits de tiers.",
        ],
      },
      {
        title: "7. Propriété intellectuelle",
        paragraphs: [
          <>
            Tous les contenus, textes, designs, méthodologies, structures de
            systèmes, noms de modules, images, ressources, interfaces,
            logiciels, bases visuelles, matériels formatifs et éléments de
            marque disponibles sur ZEDRO Academy appartiennent à ZEDRO Academy,
            à Académie ZEDRO ou à leurs concédants respectifs.
          </>,
          <>
            L’achat d’un abonnement ou l’accès à la plateforme ne transfère
            aucun droit de propriété intellectuelle. Il est accordé uniquement
            une licence limitée, personnelle, révocable, non exclusive et non
            transférable pour accéder aux contenus et les utiliser dans la
            plateforme pendant la période d’accès correspondante.
          </>,
        ],
      },
      {
        title: "8. Contenu créé par l’utilisateur",
        paragraphs: [
          <>
            Certaines fonctions de ZEDRO Academy permettent d’écrire,
            d’enregistrer, de conserver ou de générer du contenu personnel,
            comme des réponses, réflexions, journaux, évidences, micro-missions,
            demandes, décisions, registres de progression et préférences de
            lecture.
          </>,
          <>
            Le contenu que vous saisissez demeure le vôtre. Toutefois, vous
            accordez à ZEDRO Academy une licence limitée pour héberger,
            stocker, traiter, afficher et utiliser ce contenu dans votre compte
            afin d’exploiter la plateforme, conserver votre progression,
            personnaliser votre expérience, fournir l’assistance, maintenir la
            sécurité technique et respecter les obligations légales.
          </>,
          <>
            Vous êtes responsable du contenu que vous saisissez dans la
            plateforme. Vous ne devez pas introduire d’information portant
            atteinte aux droits de tiers ni de contenu illégal, offensant,
            nuisible ou contraire à l’utilisation permise.
          </>,
        ],
      },
      {
        title: "9. Confidentialité et données personnelles",
        paragraphs: [
          <>
            ZEDRO Academy peut recueillir et conserver les informations
            nécessaires à l’exploitation de la plateforme, notamment les
            informations de compte, l’adresse courriel, l’état de l’abonnement,
            la progression, les réponses, les registres de journal, les
            préférences, l’activité dans la plateforme, les données techniques et
            les communications d’assistance.
          </>,
          <>
            Les informations saisies par les utilisateurs peuvent être stockées
            dans des bases de données et services technologiques utilisés par
            ZEDRO Academy pour fournir le service. Le traitement des
            renseignements personnels est expliqué plus en détail dans la
            Politique de confidentialité.
          </>,
          <>
            ZEDRO Academy met en place des mesures raisonnables de sécurité pour
            protéger l’information. Toutefois, aucun système numérique ne peut
            garantir une sécurité, disponibilité ou récupération absolue face à
            des défaillances techniques, erreurs humaines, pertes d’accès,
            attaques, incidents de sécurité ou événements hors de contrôle
            raisonnable.
          </>,
        ],
      },
      {
        title: "10. Fonctions basées sur l’IA",
        paragraphs: [
          <>
            Certaines fonctions actuelles ou futures de ZEDRO Academy peuvent
            utiliser des outils d’intelligence artificielle pour soutenir
            l’organisation, la réflexion, la recherche, l’écriture ou
            l’accompagnement général de l’utilisateur.
          </>,
          <>
            Les réponses générées avec l’appui de l’IA peuvent contenir des
            erreurs, imprécisions ou interprétations incomplètes. L’utilisateur
            doit exercer son jugement et ne doit pas considérer ces résultats
            comme un avis professionnel, médical, psychologique, juridique,
            financier ou spirituel personnalisé.
          </>,
          <>
            Nous recommandons de ne pas saisir d’informations hautement
            sensibles ou non nécessaires dans les fonctions assistées par l’IA.
          </>,
        ],
      },
      {
        title: "11. Produits et services payants, prix, devise et taxes",
        paragraphs: [
          <>
            {OPERATOR.platformName} peut offrir des abonnements, des produits
            numériques et d’autres services payants. Le prix, la devise, les
            taxes applicables, le mode de paiement unique ou récurrent, la
            fréquence de facturation, les restrictions et les conditions
            particulières de chaque offre sont affichés dans la Boutique et au
            cours du processus de paiement avant que l’utilisateur confirme la
            transaction.
          </>,
          <>
            Le prix applicable est celui que l’utilisateur a pu vérifier et
            accepter au moment de compléter l’achat ou l’abonnement. Une
            modification ultérieure du prix public ne modifie pas
            rétroactivement une transaction déjà conclue ni une période déjà
            payée.
          </>,
          <>
            Avant de compléter le paiement, la Boutique et Stripe permettent de
            vérifier la description de l’offre, le montant applicable, la
            devise, le mode de facturation et les autres conditions essentielles
            de la transaction.
          </>,
          <>
            La banque, l’émetteur de la carte ou d’autres tiers peuvent imposer
            des frais externes, notamment des frais de conversion de devise, qui
            ne sont pas contrôlés par {OPERATOR.platformName}.
          </>,
          <>
            Les paiements sont traités par Stripe. {OPERATOR.platformName} ne
            conserve pas les numéros complets de cartes de crédit ou de débit.
          </>,
        ],
      },
      {
        title:
          "12. Essai gratuit réservé à l’abonnement, réactivation, renouvellement et confirmation",
        paragraphs: [
          <>
            L’essai gratuit de 7 jours s’applique exclusivement à l’abonnement à
            {" "}{OPERATOR.platformName}. Il ne s’applique pas aux autres
            produits ou services vendus séparément.
          </>,
          <>
            Une personne admissible qui n’a jamais utilisé l’essai peut
            bénéficier de ces 7 jours gratuits avec une carte enregistrée.
            Aucun premier paiement de l’abonnement n’est prélevé pendant cette
            période.
          </>,
          <>
            L’essai gratuit est accordé une seule fois à chaque utilisateur
            admissible. Lorsqu’une personne a déjà utilisé l’essai ou réactive
            un abonnement annulé, le nouvel abonnement est facturé
            immédiatement au prix affiché avant la confirmation.
          </>,
          <>
            Avant la confirmation de l’abonnement, la durée de l’essai, le prix
            facturé à la fin de celui-ci, la fréquence de renouvellement et la
            date prévue du premier prélèvement sont affichés.
          </>,
          <>
            Si l’utilisateur annule avant la date et l’heure de fin indiquées
            dans la confirmation, le premier paiement de l’abonnement n’est pas
            prélevé.
          </>,
          <>
            À défaut d’annulation avant la fin de l’essai, l’abonnement entre
            automatiquement en vigueur au prix, selon la fréquence de
            facturation et avec les taxes applicables affichés et acceptés avant
            sa confirmation.
          </>,
          <>
            Après le premier paiement, l’abonnement est renouvelé selon la
            fréquence acceptée jusqu’à son annulation ou jusqu’à la fin du
            service conformément aux présentes Conditions et à la loi
            applicable.
          </>,
          <>
            Après la confirmation de l’abonnement, {OPERATOR.legalTradeName}
            transmet à l’adresse courriel fournie une confirmation que
            l’utilisateur peut conserver et imprimer. Cette communication
            contient les renseignements essentiels sur l’abonnement, les dates
            applicables, le prix accepté, le renouvellement, l’annulation et
            l’accès aux documents juridiques en vigueur.
          </>,
        ],
      },
      {
        title: "13. Annulation et remboursements",
        paragraphs: [
          <>
            Vous pouvez annuler l’abonnement à partir de « Mon compte » ou en
            écrivant à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>,
          <>
            L’annulation empêche les renouvellements futurs. Si elle est
            effectuée pendant l’essai gratuit avant sa fin, le premier paiement
            n’est pas prélevé.
          </>,
          <>
            Si vous annulez après un paiement mensuel, l’accès est maintenu
            jusqu’à la fin de la période payée, sauf si la loi applicable
            prévoit un droit différent.
          </>,
          <>
            Aucun remboursement proportionnel n’est accordé pour les jours non
            utilisés ou pour l’annulation anticipée du mois en cours, sauf
            lorsque la loi applicable l’exige.
          </>,
          <>
            Les erreurs de facturation, doubles prélèvements, fraudes établies
            ou situations donnant ouverture à un droit légal de résolution ou
            de remboursement sont examinés et corrigés, le cas échéant.
          </>,
          <>
            Si {OPERATOR.legalTradeName} met définitivement fin au service sans
            manquement de l’utilisateur et ne peut fournir une partie déjà
            payée, elle effectuera l’ajustement ou le remboursement exigé par la
            loi applicable.
          </>,
        ],
      },
      {
        title: "14. Contestations de paiement et rétrofacturations",
        paragraphs: [
          <>
            Les paiements effectués par carte ou par d’autres méthodes traitées
            par des fournisseurs externes peuvent être soumis à des processus de
            contestation, de rétrofacturation ou de révision par l’émetteur du
            mode de paiement, les réseaux de paiement ou le fournisseur
            concerné.
          </>,
          <>
            En cas de contestation ou de rétrofacturation, ZEDRO Academy pourra
            présenter des preuves de l’accès accordé, de l’acceptation des
            conditions, de l’utilisation de la plateforme, de l’historique de
            facturation et des communications liées, conformément aux règles du
            fournisseur de paiement et à la loi applicable.
          </>,
        ],
      },
      {
        title: "15. Disponibilité, changements et améliorations",
        paragraphs: [
          <>
            {OPERATOR.platformName} s’efforce de maintenir la plateforme
            accessible et fonctionnelle, mais ne garantit pas l’absence totale
            d’erreurs, la compatibilité avec tous les appareils ni un
            fonctionnement ininterrompu.
          </>,
          <>
            Des travaux d’entretien, corrections, améliorations de design, mises
            à jour de contenu et ajustements techniques ou de sécurité peuvent
            être effectués, pourvu qu’ils ne réduisent pas la nature essentielle
            du service souscrit.
          </>,
          <>
            Les modifications du prix, des obligations principales ou d’autres
            éléments contractuels sont régies par la section 20 des présentes
            Conditions.
          </>,
        ],
      },
      {
        title: "16. Résultats personnels",
        paragraphs: [
          <>
            ZEDRO Academy offre une formation, une structure et des outils
            pratiques, mais ne garantit pas de résultats spécifiques. Les
            progrès personnels dépendent de multiples facteurs, notamment la
            constance de l’utilisateur, son contexte, ses décisions, son
            application pratique et ses circonstances personnelles.
          </>,
          <>
            Aucun contenu ne doit être interprété comme une promesse de
            transformation automatique ou de résultat spirituel, émotionnel,
            économique, professionnel, relationnel, physique ou personnel
            garanti.
          </>,
        ],
      },
      {
        title: "17. Suspension ou fin de l’accès",
        paragraphs: [
          <>
            {OPERATOR.legalTradeName} peut suspendre ou limiter un compte en cas
            de manquement aux présentes Conditions, de défaut de paiement, de
            fraude, d’abus, de risque de sécurité, d’exigence légale ou de
            conduite portant atteinte à la plateforme ou à des tiers.
          </>,
          <>
            Lorsque cela est raisonnable, l’utilisateur en sera avisé. En cas
            d’urgence liée à la fraude, à l’abus ou à la sécurité, la mesure
            peut être prise sans préavis, dans la mesure permise par la loi.
          </>,
          <>
            Lorsque la suspension ou la fin de l’accès est attribuable à
            l’utilisateur, elle n’efface pas les obligations de paiement déjà
            nées et n’entraîne pas automatiquement un remboursement, sous
            réserve des droits impératifs applicables.
          </>,
        ],
      },
      {
        title: "18. Liens et services de tiers",
        paragraphs: [
          <>
            La plateforme peut intégrer ou lier des services externes, comme des
            processeurs de paiement, fournisseurs de courriel, stockage,
            analytique, hébergement, outils de communication ou ressources
            complémentaires. L’utilisation de ces services peut être soumise à
            leurs propres conditions et politiques.
          </>,
          <>
            ZEDRO Academy ne contrôle pas entièrement les pratiques,
            disponibilités ou politiques de tiers, bien qu’elle cherche à
            travailler avec des fournisseurs fiables et raisonnables pour
            l’exploitation de la plateforme.
          </>,
        ],
      },
      {
        title: "19. Responsabilité",
        paragraphs: [
          <>
            Dans la mesure permise par la loi,{" "}
            {OPERATOR.legalTradeName} n’est pas responsable des pertes indirectes
            découlant d’événements hors de son contrôle raisonnable, d’une
            utilisation contraire aux présentes Conditions ou de décisions
            prises exclusivement par l’utilisateur à partir de contenu général.
          </>,
          <>
            Rien dans les présentes Conditions n’exclut ni ne limite la
            responsabilité découlant d’une faute intentionnelle ou d’une faute
            lourde, d’un préjudice corporel ou moral, ni les droits ou
            obligations qui ne peuvent être exclus ou limités par la loi.
          </>,
        ],
      },
      {
        title: "20. Modification des présentes Conditions ou de l’abonnement",
        paragraphs: [
          <>
            Dans la mesure permise par la loi, les éléments suivants peuvent
            être modifiés : le prix et la périodicité futurs, les
            fonctionnalités ou ressources incluses, les règles d’accès
            progressif, les exigences techniques et opérationnelles, ainsi que
            les dispositions des présentes Conditions.
          </>,
          <>
            Lorsqu’une modification augmente les obligations de l’utilisateur
            ou réduit celles de l’exploitante, un avis écrit, clair et lisible
            est transmis au moins 30 jours avant son entrée en vigueur. L’avis
            contient la nouvelle clause ou la clause modifiée, la version
            antérieure, la date d’entrée en vigueur et les droits de
            l’utilisateur.
          </>,
          <>
            L’utilisateur peut refuser la modification et résilier l’abonnement
            sans frais, pénalité ni indemnité en transmettant un avis à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>{" "}
            au plus tard 30 jours suivant l’entrée en vigueur de la
            modification, lorsque celle-ci augmente ses obligations ou réduit
            celles de l’exploitante.
          </>,
          <>
            Aucune modification ne s’applique rétroactivement à une période déjà
            payée, sauf si la loi le permet ou l’exige.
          </>,
        ],
      },
      {
        title: "21. Loi applicable",
        paragraphs: [
          <>
            Les présentes Conditions sont interprétées conformément aux lois
            applicables dans la province de Québec et aux lois fédérales du
            Canada qui s’appliquent, sans préjudice des droits obligatoires qui
            peuvent protéger le consommateur selon son lieu de résidence.
          </>,
          <>
            Si une disposition des présentes Conditions est jugée invalide ou
            inapplicable, les autres dispositions conserveront leur validité dans
            toute la mesure permise par la loi.
          </>,
        ],
      },
      {
        title: "22. Contact",
        paragraphs: [
          <>
            Pour toute question concernant les présentes Conditions,
            l’abonnement, les paiements, l’annulation ou l’accès, écrivez à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>,
          <>
            Pour les questions administratives ou juridiques, écrivez à{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
            .
          </>,
        ],
      },
    ],
  },
};

export default function TermsConditions() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = TERMS_COPY[language] || TERMS_COPY.es;

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.lastUpdatedLabel}</strong> {copy.lastUpdated}
      </p>

      {copy.intro.map((paragraph, index) => (
        <p key={`intro-${index}`}>{paragraph}</p>
      ))}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={{ color: "#264763" }}>{section.title}</h2>

          {section.paragraphs?.map((paragraph, index) => (
            <p key={`${section.title}-p-${index}`}>{paragraph}</p>
          ))}

          {Array.isArray(section.list) && (
            <ul>
              {section.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
